import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FiUser, FiCopy, FiCheck, FiLogOut } from 'react-icons/fi'
import { useStore } from '@/stores/useStore'
import { useNavigate } from 'react-router-dom'
import LoginModal from '@/components/LoginModal'
import { getModelsList } from '@/services/api'

export default function Profile() {
  useTranslation() // Keep for future i18n support
  const navigate = useNavigate()
  const { isAuthenticated, userEmail, logout, history } = useStore()
  const [copied, setCopied] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [serverModelCount, setServerModelCount] = useState(0)

  // Fetch model count from server
  useEffect(() => {
    const fetchStats = async () => {
      if (!isAuthenticated) {
        return
      }
      
      try {
        const { models } = await getModelsList()
        setServerModelCount(models?.length || 0)
      } catch (err) {
        console.error('Failed to fetch models:', err)
      }
    }
    
    fetchStats()
  }, [isAuthenticated])

  const handleCopyEmail = () => {
    if (userEmail) {
      navigator.clipboard.writeText(userEmail)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  // Calculate stats - combine server and local history
  const totalModels = Math.max(serverModelCount, history.length)
  const syncedModels = serverModelCount

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-luxury-navy-950 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border border-luxury-gold-800/30 p-8 text-center">
            <div className="w-24 h-24 bg-luxury-navy-800 rounded-full mx-auto mb-6 flex items-center justify-center">
              <FiUser className="w-12 h-12 text-gray-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">用户中心</h1>
            <p className="text-gray-400 mb-6">请先登录以查看您的账户信息</p>
            <button
              onClick={() => setLoginModalOpen(true)}
              className="px-6 py-3 bg-gradient-to-r from-luxury-gold-600 to-luxury-gold-500 text-luxury-navy-950 font-semibold rounded-lg hover:from-luxury-gold-500 hover:to-luxury-gold-400 transition-colors"
            >
              连接钱包登录
            </button>
          </div>
        </div>
        <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-luxury-navy-950 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Profile Header */}
        <div className="bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border border-luxury-gold-800/30 p-8 mb-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 bg-gradient-to-br from-luxury-gold-600 to-luxury-gold-400 rounded-full flex items-center justify-center">
              <FiUser className="w-12 h-12 text-luxury-navy-950" />
            </div>
            
            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-2xl font-bold text-white mb-2">用户中心</h1>
              
              {/* User Email */}
              <div className="flex items-center justify-center md:justify-start gap-2 mb-4">
                <span className="text-gray-400">邮箱:</span>
                <code className="px-3 py-1 bg-luxury-navy-800 rounded text-luxury-gold-500 font-mono">
                  {userEmail || ''}
                </code>
                <button
                  onClick={handleCopyEmail}
                  className="p-2 text-gray-400 hover:text-luxury-gold-500 transition-colors"
                  title="复制邮箱"
                >
                  {copied ? <FiCheck className="w-4 h-4 text-green-500" /> : <FiCopy className="w-4 h-4" />}
                </button>
              </div>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors"
              >
                <FiLogOut className="w-4 h-4" />
                <span>退出登录</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border border-luxury-gold-800/30 p-6 text-center">
            <div className="text-3xl font-bold text-luxury-gold-500 mb-2">{totalModels}</div>
            <div className="text-gray-400">生成的模型</div>
          </div>
          <div className="bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border border-luxury-gold-800/30 p-6 text-center">
            <div className="text-3xl font-bold text-luxury-gold-500 mb-2">{syncedModels}</div>
            <div className="text-gray-400">已上架 NFT</div>
          </div>
          <div className="bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border border-luxury-gold-800/30 p-6 text-center">
            <div className="text-3xl font-bold text-luxury-gold-500 mb-2">0</div>
            <div className="text-gray-400">已售出</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border border-luxury-gold-800/30 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">快捷操作</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/create')}
              className="p-4 bg-luxury-navy-800 rounded-lg text-left hover:bg-luxury-navy-700 transition-colors group"
            >
              <div className="text-lg font-semibold text-white group-hover:text-luxury-gold-500 transition-colors">
                创建新模型
              </div>
              <div className="text-sm text-gray-400">上传图片生成 3D 模型</div>
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="p-4 bg-luxury-navy-800 rounded-lg text-left hover:bg-luxury-navy-700 transition-colors group"
            >
              <div className="text-lg font-semibold text-white group-hover:text-luxury-gold-500 transition-colors">
                我的模型
              </div>
              <div className="text-sm text-gray-400">查看和管理已生成的模型</div>
            </button>
          </div>
        </div>
      </div>

      <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </div>
  )
}
